
import axios from 'axios'
import { Component, Mixins, Prop } from 'vue-property-decorator'
import UserMixin from '@/mixins/UserMixin'
import { ExploreModule } from '@/store/modules/explore'
import { ICurrencyDisplaySettings, IProjectDetailSettings } from '@/types/cms'
import { ICoFunding, IPaymentProvider } from '@/types/finances'
import { IExploreProjectDetail, IPrivateDonationForm, IPrivateDonationFormErrors } from '@/types/projects'
import { API_URLS } from "@/utils/helpers"
import ProjectInfo from './private_donation/ProjectInfo.vue'
import DonationForm from './private_donation/DonationForm.vue'
import SelectPaymentProvider from './private_donation/SelectPaymentProvider.vue'
import EmailInput from '../processing_steps/commons/fields/EmailInput.vue'
import ToastMixin from '@/mixins/ToastMixin'
import DecoFooter from '../detail_pages/deco_bar/DecoFooter.vue'

@Component({
  name: 'project-detail',
  components: {
    ProjectInfo,
    DonationForm,
    SelectPaymentProvider,
    EmailInput,
    DecoFooter
  }
})
export default class NewPrivateDonationArea extends Mixins(UserMixin, ToastMixin) {
  @Prop({ default: false }) isOrganization!: boolean

  providerView = false
  project: IExploreProjectDetail = null
  amountValid = false
  showManualBankTransferModal = false

  handleAmountInvalid () {
    this.amountValid = false
    this.formErrors.amount = ""
  }

  requiredFields = ["first_name", "last_name", "street", "city", "postal_code", "country"]

  formErrors: IPrivateDonationFormErrors = {
    amount: '',
    first_name: '',
    last_name: '',
    email: '',
    street: '',
    city: '',
    country: '',
    region: '',
    postal_code: '',
    additional_information: '',
    terms_accepted: '',
    privacy_accepted: ''
  }

  privateDonationForm: IPrivateDonationForm = {
    term_of_address: 1,
    academic_title: 1,
    amount: null,
    first_name: "",
    last_name: "",
    company: "",
    email: "",
    street: '',
    city: '',
    country: 'DE',
    region: '',
    postal_code: '',
    additional_information: '',
    terms_accepted: false,
    privacy_accepted: false,
    needs_receipt: null,
    takes_fee: false,
    anonymus_in_info_mail: false
  }

  manualBankTransferData = {
    bank_name: '',
    bic: '',
    iban: ''
  }

  terms_of_address = [
    {
      text: this.$gettext("Term of address"),
      value: 1
    },
    {
      text: this.$gettext("Mrs."),
      value: 2
    },
    {
      text: this.$gettext("Mr."),
      value: 3
    }
  ]

  academic_titles = [
    {
      text: this.$gettext('Academic title'),
      value: 1
    },
    {
      text: this.$gettext('Doctor'),
      value: 2
    },
    {
      text: this.$gettext('Professor'),
      value: 3
    }
  ]

  get projectDetailSettings (): IProjectDetailSettings {
    return ExploreModule.projectDetailSettings
  }

  get coFundings (): ICoFunding[] {
    return ExploreModule.coFundings
  }

  get isPreview (): boolean {
    return !!this.$route.query?.preview_token
  }

  get currencyDisplay (): ICurrencyDisplaySettings {
    return this.projectDetailSettings?.currencyDisplay
  }

  get orgaColor (): string {
    const baseColor = getComputedStyle(document.documentElement).getPropertyValue('--base-ci').trim()
    const orgaColor = this.project?.organization.color || baseColor
    return orgaColor
  }

  get amountInCents (): number {
    if (!this.privateDonationForm.amount) return 0
    return Math.round(100 * this.privateDonationForm.amount)
  }

  gotoDonationArea (): void {
    this.$router.push({
      path: `/project/${this.project.slug}/donate`
    })
  }

  async mounted () {
    await this.loadProject()
    await this.getBankData()
  }

  async getBankData (): Promise<void> {
    const apiURL = API_URLS.FINANCES.BANK_ACCOUNTS.RETRIEVE('private-donation-bank-account')
    await axios.get(apiURL).then(res => {
      this.manualBankTransferData.bank_name = res.data.bank_name
      this.manualBankTransferData.bic = res.data.bic
      this.manualBankTransferData.iban = res.data.iban
    })
  }

  changeAnoymusInInfoMail (value: boolean) {
    this.privateDonationForm.anonymus_in_info_mail = value
  }

  formatCurrency (val: number): string {
    const displayName = this.currencyDisplay.useCurrency ? this.currencyDisplay.currencySymbol : this.currencyDisplay.coinNamePlural
    return `${val} ${displayName}`
  }

  updateNavBalance (): void {
    document.querySelectorAll<HTMLElement>('.coin-balance').forEach(balance => {
      balance.innerText = this.currencyDisplay.useCurrency ? this.userProfile.balance.in_currency_display : this.formatCurrency(this.userProfile.balance.in_coins)
    })
  }

  updateUserBalance (): void {
    this.fetchUserBalance().then(() => {
      this.updateNavBalance()
    })
  }

  async updateNavbar (): Promise<void> {
    await axios.get(API_URLS.V3.TOPBAR_DATA).then(res => {
      document.querySelector<HTMLElement>('.adjust-navbar').innerHTML = res.data.template
    })
    this.updateNavBalance()
    this.loadProject()
  }

  async loadProject (): Promise<void> {
    this.$wait.start('load project')
    let previewToken = this.$route.query?.preview_token
    if (Array.isArray(previewToken)) {
      previewToken = previewToken[0]
    }

    let url = ''
    if (this.isOrganization) {
      url = API_URLS.EXPLORE.ORGANIZATIONS.RETRIEVE(this.$route.params.orgSlug)
      if (previewToken) {
        url = API_URLS.EXPLORE.ORGANIZATIONS.PREVIEW(this.$route.params.orgSlug, previewToken)
      }
    } else {
      url = API_URLS.EXPLORE.PROJECTS.RETRIEVE(this.$route.params.projectSlug)
      if (previewToken) {
        url = API_URLS.EXPLORE.PROJECTS.PREVIEW(this.$route.params.projectSlug, previewToken)
      }
    }

    if (ExploreModule.projectDetailSettings) {
      await axios.get(url).then(res => {
        this.project = res.data
      })
    } else {
      await Promise.all([
        ExploreModule.fetchProjectDetailSettings(),
        ExploreModule.fetchCofundings(),
        axios.get(url).then(res => {
          this.project = res.data
        })
      ])
    }

    this.$wait.end('load project')
  }

  setExtraDataForPrivateDonation (): void {
    const urlParams = new URLSearchParams(window.location.search)
    const showcaseParam = urlParams.get('showcase')
    if (showcaseParam) {
      localStorage.setItem('donation_extra_data', showcaseParam)
    } else {
      localStorage.setItem('donation_extra_data', "")
    }
  }

  async created (): Promise<void> {
    this.fetchUserProfile()
    await this.loadProject()
    this.setExtraDataForPrivateDonation()

    document.title = `${this.project.title} - ${this.projectDetailSettings?.siteName}`
  }

  gotoPaymentMethods (): void {
    this.formErrors.amount = ""
    if (!this.amountValid) {
      this.makeToast("danger", this.$gettext('Please enter a donation amount'), this.$gettext('The donation amount is required.'))
      this.formErrors.amount = this.$gettext('Please enter a donation amount.')
      return
    }

    if (!this.validateInput() || !this.validateTermsAndPrivacy()) {
      this.makeToast("danger", this.$gettext('Error'), this.$gettext('Please fill in all required fields correctly.'))
      return
    }
    this.providerView = true
  }

  clearErrors () {
    for (const key in this.formErrors) {
      this.formErrors[key] = ""
    }
  }

  validateFoerderAppCheckBox (): boolean {
    if (this.privateDonationForm.anonymus_in_info_mail === null) {
      return false
    }
    return true
  }

  validateInput () {
    this.clearErrors()
    let validated = true
    const emailInput = new EmailInput()
    const validatedEmail = emailInput.validateEmail(this.privateDonationForm.email)
    if (!this.validateFoerderAppCheckBox()) {
      validated = false
      this.makeToast("danger", this.$gettext('Please select an option'), this.$gettext('Choose whether you want to remain anonymous.'))
    }
    if (!this.amountValid) {
      validated = false
      this.formErrors.amount = this.$gettext('Please enter a donation amount.')
    }
    if (!validatedEmail) {
      this.formErrors.email = this.$gettext("Please enter a valid email address")
      validated = false
    }
    this.requiredFields.forEach(fieldName => {
      const value = this.privateDonationForm[fieldName]
      if (!value) {
        this.formErrors[fieldName] = this.$gettext("This field is required")
        validated = false
      }
    })
    return validated
  }

  getNetValueWithFeesInCents (value: number, paymentProvider: IPaymentProvider): number {
    if (this.privateDonationForm.takes_fee) {
      return Math.round(
        (value + parseFloat(paymentProvider.fee_base_in_cents)) / (1.0 - parseFloat(paymentProvider.fee_factor))
      )
    } else {
      return value
    }
  }

  validateTermsAndPrivacy (): boolean {
    let isValid = true
    if (this.projectDetailSettings.showTermsOfUseCheckbox && !this.privateDonationForm.terms_accepted) {
      this.formErrors.terms_accepted = this.$gettext("Please accept the terms of use")
      isValid = false
    }
    if (this.projectDetailSettings.showPrivacyAgreementCheckbox && !this.privateDonationForm.privacy_accepted) {
      this.formErrors.privacy_accepted = this.$gettext("Please accept the privacy policy")
      isValid = false
    }
    return isValid
  }

  async donateWithManualBankTransfer () {
    if (!this.validateInput() || !this.validateTermsAndPrivacy()) {
      return
    }
    const data = {
      project: this.project.slug,
      amount_in_cents: this.amountInCents,
      first_name: this.privateDonationForm.first_name,
      last_name: this.privateDonationForm.last_name,
      email: this.privateDonationForm.email,
      street: this.privateDonationForm.street,
      city: this.privateDonationForm.city,
      country: this.privateDonationForm.country,
      postal_code: this.privateDonationForm.postal_code,
      additional_information: this.privateDonationForm.additional_information,
      terms: this.privateDonationForm.terms_accepted,
      privacy: this.privateDonationForm.privacy_accepted,
      needs_receipt: this.privateDonationForm.needs_receipt,
      quantity: 1,
      currency_code: "EUR"
    }
    await axios.post(API_URLS.MANUAL_BANK_TRANSFER.DONATE, data).then(() => {
      this.showManualBankTransferModal = true
    })
  }

  async donate (paymentProvider: IPaymentProvider): Promise<void> {
    const valueWithFees = this.getNetValueWithFeesInCents(
      this.amountInCents,
      paymentProvider
    )

    const data = {
      company: this.privateDonationForm.company,
      term_of_address: this.privateDonationForm.term_of_address,
      academic_title: this.privateDonationForm.academic_title,
      project: this.project.slug,
      net_value_in_cents: this.amountInCents,
      amount_in_cents: valueWithFees,
      first_name: this.privateDonationForm.first_name,
      last_name: this.privateDonationForm.last_name,
      email: this.privateDonationForm.email,
      street: this.privateDonationForm.street,
      city: this.privateDonationForm.city,
      country: this.privateDonationForm.country,
      postal_code: this.privateDonationForm.postal_code,
      additional_information: this.privateDonationForm.additional_information,
      terms: this.privateDonationForm.terms_accepted,
      privacy: this.privateDonationForm.privacy_accepted,
      needs_receipt: this.privateDonationForm.needs_receipt,
      quantity: 1,
      currency_code: "EUR",
      provider: paymentProvider.slug,
      takes_fee: this.privateDonationForm.takes_fee,
      extra_data: localStorage.getItem("donation_extra_data") || "",
      promoter: null,
      anonymus_in_info_mail: this.privateDonationForm.anonymus_in_info_mail
    }

    data.promoter = this.project.organization.slug

    axios.post(API_URLS.PRIVATE_PAYMENT_REQUESTS.DONATE, data).then(response => {
      window.location = response.data.payment_url
    }).catch(error => {
      if (error.response && error.response.data) {
        if (!Array.isArray(error.response.data)) {
          this.makeToast("danger", this.$gettext('Whoops!'), this.$gettext('an error occurred'))
          return
        }
        Object.entries(error.response.data).forEach(entry => {
          const [key, value] = entry
          const errorMsg = Array.isArray(value) ? value[0] : value
          if (key in this.formErrors) {
            this.formErrors[key] = errorMsg
          } else {
            this.makeToast("danger", key, errorMsg)
          }
        })
      }
    })
  }

  handleAmountValid () {
    this.amountValid = true
  }
}
